import React, { useState, useEffect, useRef, useContext } from 'react'; // React importları, useRef, useContext eklendi
import { useParams, useNavigate, Link } from 'react-router-dom'; // Router importları
import { API_URL } from '../config/constants'; // API URL importu
// CollapseProps, TableProps ayrı import edildi
import { Spin, Modal as AntModal, notification, Descriptions, Image, List, Table, Tag, Button as AntButton, Space, Carousel, Empty, Collapse, Divider, Tabs } from 'antd'; // Tabs eklendi
import type { CollapseProps, TableProps } from 'antd'; // Tür importları ayrıldı
import { LoadingOutlined, EditOutlined, DeleteOutlined, LeftOutlined, RightOutlined, ExpandOutlined, DownloadOutlined, EyeOutlined, FilePdfOutlined, FileOutlined, FileExcelOutlined, FileZipOutlined, FileImageOutlined } from '@ant-design/icons'; // Ant Design ikonları
import { AuthContext } from '../context/AuthContext'; // AuthContext importu

interface Accessory {
  name: string;
  quantity: number;
  description?: string;
}


interface Color {
  _id: string;
  name: string;
  firmCode?: string;
  tcxCode?: string;
  hexCode?: string; // hexCode alanı eklendi
}

// Teknik föy için ek interface'ler
interface SizeSpec {
  size: string;
  chest: number;
  waist: number;
  hip: number;
  length: number;
  sleeve: number;
  [key: string]: string | number; // Dinamik ölçü alanları için
}

interface StitchDetail {
  location: string;
  stitchType: string;
  threadType: string;
  stitchesPerInch: number;
  notes: string;
  imageUrls?: string[]; // Görsel URL'leri eklendi (opsiyonel)
}

interface FabricSpec {
  composition: string;
  weight: string;
  construction: string;
  supplier: string;
  colorFastness: string;
  shrinkage: string;
}

interface PrintingDetail {
  technique: string;
  location: string;
  colors: string;
  size: string;
  notes: string;
  imageUrls?: string[]; // Görsel URL'leri eklendi (opsiyonel)
}

interface TechnicalSpec {
  dynamicSizeTable?: {
    columns: string[];
    rows: string[];
    data: Record<string, Record<string, string>>;
  };
  sizeSpecs?: SizeSpec[];
  stitchDetails: StitchDetail[];
  fabricSpecs: FabricSpec[];
  printingDetails: PrintingDetail[];
  technicalDrawingUrls?: string[];
  measurementGuideUrls?: string[];
  careInstructions: string[];
  qualityStandards: string[];
  notes: string;
  modelDocumentUrls?: string[];
}

// Product tipi düzeltildi, color artık bir Color objesi
interface Product {
  _id: string;
  modelName: string;
  purchasingGroup: string; // Güncellendi
  modelStyle?: string; // Yeni alan
  modelImage?: string;
  imageUrl?: string;
  imageUrls?: string[]; // Çoklu resim için dizi
  fabric: string;
  color: Color;  // Düzeltildi: string değil, Color objesi
  description: string;
  isPrototype: boolean;
  prototypeCode?: string;
  productCode?: string;
  status: string;
  accessories: Accessory[];
  technicalSpec?: TechnicalSpec; // Teknik föy
}

// Renk şeması ve ortak bileşenler için stil tanımlamaları
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xl font-semibold text-gray-800 mb-4 mt-6 first:mt-0">
    {children}
  </h3>
);

// URL birleştirme yardımcı fonksiyonu
function getFullUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return `${API_URL}/${url}`;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentModalImage, setCurrentModalImage] = useState("");
  const [selectedTab, setSelectedTab] = useState<'details' | 'technicalSpecs'>('details');
  const [colorDetails, setColorDetails] = useState<Color | null>(null); // Add state for color details
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const carouselRef = useRef<any>(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // New function to fetch color details
  const fetchColorDetails = async (colorId: string) => {
    try {
      const response = await fetch(`${API_URL}/colors/${colorId}`);
      if (!response.ok) {
        console.warn('Renk detayları getirilemedi');
        return null;
      }
      const colorData = await response.json();
      console.log("Renk detayları:", colorData);
      return colorData;
    } catch (err) {
      console.error("Renk detayları getirme hatası:", err);
      return null;
    }
  };

  const fetchProduct = async () => {
    try {
      // API'ye istek gönderme
      const response = await fetch(`${API_URL}/products/${id}`);
      if (!response.ok) {
        throw new Error('Ürün bulunamadı');
      }
      const data = await response.json();
      
      // Veri tipini kontrol et, color alanının populate edildiğinden emin ol
      console.log("Alınan ürün verisi:", data);
      
      setProduct(data);
      
      // Eğer renk ID'si varsa ve bir obje değilse (populate edilmemişse), renk detaylarını getir
      if (data.color && typeof data.color !== 'object') {
        const colorId = data.color;
        const colorData = await fetchColorDetails(colorId);
        if (colorData) {
          setColorDetails(colorData);
        }
      } else if (data.color && typeof data.color === 'object') {
        // Eğer renk zaten populate edilmişse, direkt olarak state'e ata
        setColorDetails(data.color);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Ürün getirme hatası:", err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/products/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Silme işlemi başarısız oldu');

      navigate('/products');
    } catch (error) {
      setError('Ürün silinirken bir hata oluştu');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Taslak';
      case 'active':
        return 'Aktif';
      case 'archived':
        return 'Arşivlenmiş';
      default:
        return status;
    }
  };

  // Tüm ürün görselleri için dizi hazırlama
  const getProductImages = () => {
    if (!product) return [];
    
    const images: string[] = [];
    
    // Çoklu resimler varsa ekle
    if (product.imageUrls && product.imageUrls.length > 0) {
      images.push(...product.imageUrls.map(url => getFullUrl(url)));
    } 
    // Tek modelImage varsa ekle
    else if (product.modelImage) {
      images.push(getFullUrl(product.modelImage));
    } 
    // Eski sürüm imageUrl varsa ekle
    else if (product.imageUrl) {
      images.push(getFullUrl(product.imageUrl));
    }
    
    return images;
  };

  // Renk bilgisini güvenli bir şekilde görüntüleme - update to use colorDetails state
  const renderColorInfo = () => {
    // Eğer colorDetails varsa, o bilgileri kullan
    if (colorDetails) {
      // Ana renk adı
      const colorName = colorDetails.name || 'İsimsiz renk';
      
      // Firma kodu veya TCX kodu varsa, parantez içinde göster
      const colorCodes = [];
      if (colorDetails.firmCode) colorCodes.push(`Firma: ${colorDetails.firmCode}`);
      if (colorDetails.tcxCode) colorCodes.push(`TCX: ${colorDetails.tcxCode}`);
      
      const colorCodeText = colorCodes.length > 0 ? ` (${colorCodes.join(', ')})` : '';
      
      return colorName + colorCodeText;
    }
    
    // product.color hala var ama colorDetails state'i yok ise
    if (product && product.color) {
      // Eğer color bir obje ise (populate edilmişse)
      if (typeof product.color === 'object' && product.color !== null) {
        const colorName = product.color.name || 'İsimsiz renk';
        
        const colorCodes = [];
        if (product.color.firmCode) colorCodes.push(`Firma: ${product.color.firmCode}`);
        if (product.color.tcxCode) colorCodes.push(`TCX: ${product.color.tcxCode}`);
        
        const colorCodeText = colorCodes.length > 0 ? ` (${colorCodes.join(', ')})` : '';
        
        return colorName + colorCodeText;
      }
      
      // Color populate edilmemişse, ID'yi göster ve uyarı ekle
      return `${product.color} (Renk bilgisi yüklenemedi)`;
    }
    
    return "Renk belirtilmemiş";
  };

  // Teknik Föy Render İşlemleri
  const renderTechnicalSpecs = () => {
    if (!product?.technicalSpec) {
      return (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Teknik föy bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">Bu ürün için teknik föy bilgisi bulunmamaktadır.</p>
        </div>
      );
    }

    const { 
      dynamicSizeTable,
      sizeSpecs, 
      fabricSpecs, 
      stitchDetails, 
      printingDetails, 
      technicalDrawingUrls,
      measurementGuideUrls,
      modelDocumentUrls,
      careInstructions, 
      qualityStandards, 
      notes 
    } = product.technicalSpec;

    return (
      <div className="space-y-6">
        {/* Beden Tablosu - dynamicSizeTable kullanarak */}
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Beden Tablosu</h4>
          {dynamicSizeTable?.columns && dynamicSizeTable.rows ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ölçü</th>
                    {dynamicSizeTable.columns.map((col, idx) => (
                      <th key={idx} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {col}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dynamicSizeTable.rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{row}</td>
                      {dynamicSizeTable.columns.map((col, colIdx) => (
                        <td key={colIdx} className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                          {dynamicSizeTable.data[row]?.[col] || '-'}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500">Beden ölçüleri bulunamadı.</p>
        )}
        </div>
        
        {/* Kumaş Detayları */}
        {fabricSpecs && fabricSpecs.length > 0 && (
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Kumaş Detayları</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fabricSpecs.map((fabric, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {fabric.composition && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Kompozisyon</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.composition}</p>
                    </div>
                  )}
                  
                  {fabric.weight && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Gramaj</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.weight}</p>
                    </div>
                  )}
                  
                  {fabric.construction && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Yapı</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.construction}</p>
                    </div>
                  )}
                  
                  {fabric.supplier && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Tedarikçi</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.supplier}</p>
                    </div>
                  )}
                  
                  {fabric.colorFastness && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Renk Haslığı</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.colorFastness}</p>
                    </div>
                  )}
                  
                  {fabric.shrinkage && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Çekme Payı</h4>
                      <p className="text-sm font-medium text-gray-900">{fabric.shrinkage}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Dikiş ve Baskı Detayları */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Dikiş Detayları */}
          {stitchDetails && stitchDetails.length > 0 && (
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-4">Dikiş Detayları</h4>
              <div className="space-y-3">
                {stitchDetails.map((stitch, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-3">
                    <div className="flex justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">Dikiş #{index + 1}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {stitch.location && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Bölge</h4>
                          <p className="text-sm font-medium text-gray-900">{stitch.location}</p>
                        </div>
                      )}
                      
                      {stitch.stitchType && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Dikiş Tipi</h4>
                          <p className="text-sm font-medium text-gray-900">{stitch.stitchType}</p>
                        </div>
                      )}
                      
                      {stitch.threadType && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">İplik Tipi</h4>
                          <p className="text-sm font-medium text-gray-900">{stitch.threadType}</p>
                        </div>
                      )}
                      
                      {stitch.stitchesPerInch > 0 && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Dikiş Sıklığı</h4>
                          <p className="text-sm font-medium text-gray-900">{stitch.stitchesPerInch} dikiş/cm</p>
                        </div>
                      )}
                    </div>
                    
                    {stitch.notes && (
                      <div className="mt-2 border-t border-gray-200 pt-2">
                        <h4 className="text-xs font-medium text-gray-500 uppercase">Notlar</h4>
                        <p className="text-sm text-gray-600">{stitch.notes}</p>
                      </div>
                    )}

                    {/* Dikiş Görselleri */}
                    {stitch.imageUrls && stitch.imageUrls.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Görseller</h5>
                        <div className="flex flex-wrap gap-2">
                          {stitch.imageUrls.map((url, imgIndex) => (
                            <button
                              key={imgIndex}
                              onClick={() => {
                                setCurrentModalImage(getFullUrl(url));
                                setIsImageModalOpen(true);
                              }}
                              className="relative group w-16 h-16 rounded border border-gray-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <img
                                src={getFullUrl(url)}
                                alt={`Dikiş ${index + 1} - Resim ${imgIndex + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => (e.currentTarget.src = '/placeholder-image.png')}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Baskı/Nakış Detayları */}
          {printingDetails && printingDetails.length > 0 && (
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-4">Baskı/Nakış Detayları</h4>
              <div className="space-y-3">
                {printingDetails.map((print, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-3">
                    <div className="flex justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">Baskı #{index + 1}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {print.technique && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Teknik</h4>
                          <p className="text-sm font-medium text-gray-900">{print.technique}</p>
                        </div>
                      )}
                      
                      {print.location && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Konum</h4>
                          <p className="text-sm font-medium text-gray-900">{print.location}</p>
                        </div>
                      )}
                      
                      {print.colors && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Renkler</h4>
                          <p className="text-sm font-medium text-gray-900">{print.colors}</p>
                        </div>
                      )}
                      
                      {print.size && (
                        <div className="mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase">Boyut</h4>
                          <p className="text-sm font-medium text-gray-900">{print.size}</p>
                        </div>
                      )}
                    </div>
                    
                    {print.notes && (
                      <div className="mt-2 border-t border-gray-200 pt-2">
                        <h4 className="text-xs font-medium text-gray-500 uppercase">Notlar</h4>
                        <p className="text-sm text-gray-600">{print.notes}</p>
                      </div>
                    )}

                    {/* Baskı Görselleri */}
                    {print.imageUrls && print.imageUrls.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Görseller</h5>
                        <div className="flex flex-wrap gap-2">
                          {print.imageUrls.map((url, imgIndex) => (
                            <button
                              key={imgIndex}
                              onClick={() => {
                                setCurrentModalImage(getFullUrl(url));
                                setIsImageModalOpen(true);
                              }}
                              className="relative group w-16 h-16 rounded border border-gray-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <img
                                src={getFullUrl(url)}
                                alt={`Baskı ${index + 1} - Resim ${imgIndex + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => (e.currentTarget.src = '/placeholder-image.png')}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Teknik Çizimler */}
        {technicalDrawingUrls && technicalDrawingUrls.length > 0 && (
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Teknik Çizimler</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {technicalDrawingUrls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => {
                    // API_URL'yi ekleyerek tam URL oluştur
                    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
                    setCurrentModalImage(getFullUrl(fullUrl));
                    setIsImageModalOpen(true);
                  }}
                  className="relative group aspect-w-1 aspect-h-1 rounded border border-gray-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <img
                    // API_URL'yi ekleyerek tam URL oluştur
                    src={getFullUrl(url)}
                    alt={`Teknik Çizim ${index + 1}`}
                    className="w-full h-full object-contain" // object-contain daha uygun olabilir
                    onError={(e) => (e.currentTarget.src = '/placeholder-image.png')} // Placeholder yolu düzeltilmeli
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Bakım Talimatları ve Kalite Standartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Bakım Talimatları */}
          {careInstructions && careInstructions.length > 0 && (
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-4">Bakım Talimatları</h4>
              <div className="flex flex-wrap gap-2">
                {careInstructions.map((instruction, index) => (
                  <div key={index} className="inline-flex items-center bg-indigo-50 text-indigo-700 rounded-full px-3 py-1">
                    <span className="text-xs">{instruction}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Kalite Standartları */}
          {qualityStandards && qualityStandards.length > 0 && (
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-4">Kalite Standartları</h4>
              <div className="flex flex-wrap gap-2">
                {qualityStandards.map((standard, index) => (
                  <div key={index} className="inline-flex items-center bg-green-50 text-green-700 rounded-full px-3 py-1">
                    <span className="text-xs">{standard}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Ek Notlar */}
        {notes && (
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Ek Notlar</h4>
            <p className="text-gray-700 whitespace-pre-line">{notes}</p>
          </div>
        )}

        {/* Model Documents */}
        {modelDocumentUrls && modelDocumentUrls.length > 0 && (
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Model Dosyaları</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {modelDocumentUrls.map((url, index) => renderFilePreview(url, index, 'Model Dosyası'))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Helper Functions ---
  const openImageModal = (imageUrl: string) => {
    setCurrentModalImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setCurrentModalImage("");
  };

  // Define renderFilePreview function here
  const renderFilePreview = (url: string, index: number, type: string) => {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const isPdf = url.toLowerCase().endsWith('.pdf');
    return (
      <div key={`${type}-${index}`} className="aspect-square border rounded-md overflow-hidden hover:shadow-md transition-shadow flex justify-center items-center bg-gray-50 p-1">
        {isPdf ? (
          <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center text-center text-blue-600 hover:text-blue-800">
            <FilePdfOutlined style={{ fontSize: '36px' }} className="text-red-500 mb-1" />
            <span className="text-xs break-all">{url.split('/').pop()}</span>
          </a>
        ) : (
          <Image
            src={getFullUrl(url)}
            alt={`${type} ${index + 1}`}
            className="object-contain max-h-full max-w-full"
            preview={{ mask: <><EyeOutlined /> Önizle</> }}
            placeholder={<Spin size="small" />}
            fallback="/placeholder-image.png"
          />
        )}
      </div>
    );
  };

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error || 'Ürün bulunamadı'}</p>
      </div>
    );
  }

  // Görsel dizisini hazırla
  const productImages = getProductImages();

  // PDF Önizleme fonksiyonu
  const handlePreviewPdf = () => {
    if (!id) return;
    // Backend'deki PDF önizleme endpoint'ini yeni sekmede aç
    window.open(`${API_URL}/products/${id}/pdf/preview`, '_blank');
  };

  // --- Helper Functions for Formatting/Styling ---
  const getPurchasingGroupColor = (group: string) => {
    switch (group) {
      case 'A':
        return 'blue';
      case 'B':
        return 'green';
      case 'C':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatPurchasingGroup = (group: string) => {
    switch (group) {
      case 'A':
        return 'Grup A';
      case 'B':
        return 'Grup B';
      case 'C':
        return 'Grup C';
      default:
        return group;
    }
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Taslak';
      case 'active':
        return 'Aktif';
      case 'archived':
        return 'Arşivlenmiş';
      default:
        return status;
    }
  };

  // --- Collapse Items for Technical Spec ---
  const getTechnicalSpecItems = (): CollapseProps['items'] => {
    if (!product?.technicalSpec) return [];

    const items: CollapseProps['items'] = [];

    // Beden Tablosu - dynamicSizeTable kullanarak
    if (product.technicalSpec?.dynamicSizeTable && 
        product.technicalSpec.dynamicSizeTable.columns?.length > 0 && 
        product.technicalSpec.dynamicSizeTable.rows?.length > 0) {
      const dynamicTable = product.technicalSpec.dynamicSizeTable;
      items.push({
        key: 'sizeTable',
        label: 'Beden Ölçüleri',
        children: (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ölçü</th>
                  {dynamicTable.columns.map((col, idx) => (
                    <th key={idx} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dynamicTable.rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{row}</td>
                    {dynamicTable.columns.map((col, colIdx) => (
                      <td key={colIdx} className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {dynamicTable.data[row]?.[col] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      });
    } else {
      // Ölçü tablosu verisi bulunamadı mesajı göster
      items.push({
        key: 'sizeTable',
        label: 'Beden Ölçüleri',
        children: (
          <div className="text-center py-4">
            <p className="text-gray-500">Beden ölçüleri bulunamadı.</p>
          </div>
        ),
      });
    }

    // Fabric Specs
    if (product.technicalSpec.fabricSpecs && product.technicalSpec.fabricSpecs.length > 0) {
      items.push({
        key: 'fabricSpecs',
        label: 'Kumaş Özellikleri',
        children: product.technicalSpec.fabricSpecs.map((spec, index) => (
          <Descriptions key={`fabric-${index}`} bordered column={2} size="small" className="mb-4">
            {spec.composition && <Descriptions.Item label="Kompozisyon">{spec.composition}</Descriptions.Item>}
            {spec.weight && <Descriptions.Item label="Gramaj">{spec.weight}</Descriptions.Item>}
            {spec.construction && <Descriptions.Item label="Yapı">{spec.construction}</Descriptions.Item>}
            {spec.supplier && <Descriptions.Item label="Tedarikçi">{spec.supplier}</Descriptions.Item>}
            {spec.colorFastness && <Descriptions.Item label="Renk Haslığı">{spec.colorFastness}</Descriptions.Item>}
            {spec.shrinkage && <Descriptions.Item label="Çekme Payı">{spec.shrinkage}</Descriptions.Item>}
          </Descriptions>
        )),
      });
    }

    // Stitch Details
    if (product.technicalSpec.stitchDetails && product.technicalSpec.stitchDetails.length > 0) {
      items.push({
        key: 'stitchDetails',
        label: 'Dikiş Detayları',
        children: <List size="small" bordered dataSource={product.technicalSpec.stitchDetails} renderItem={(item, index) => <List.Item key={`stitch-${index}`}><Descriptions column={1} size="small" className="w-full">{item.location && <Descriptions.Item label="Bölge">{item.location}</Descriptions.Item>}{item.stitchType && <Descriptions.Item label="Dikiş Tipi">{item.stitchType}</Descriptions.Item>}{item.threadType && <Descriptions.Item label="İplik Tipi">{item.threadType}</Descriptions.Item>}{item.stitchesPerInch > 0 && <Descriptions.Item label="Dikiş Sıklığı">{item.stitchesPerInch} dikiş/cm</Descriptions.Item>}{item.notes && <Descriptions.Item label="Notlar">{item.notes}</Descriptions.Item>}</Descriptions></List.Item>} className="bg-white" />,
      });
    }

    // Printing Details
    if (product.technicalSpec.printingDetails && product.technicalSpec.printingDetails.length > 0) {
       items.push({
        key: 'printingDetails',
        label: 'Baskı/Nakış Detayları',
        children: <List size="small" bordered dataSource={product.technicalSpec.printingDetails} renderItem={(item, index) => <List.Item key={`print-${index}`}><Descriptions column={1} size="small" className="w-full">{item.technique && <Descriptions.Item label="Teknik">{item.technique}</Descriptions.Item>}{item.location && <Descriptions.Item label="Konum">{item.location}</Descriptions.Item>}{item.colors && <Descriptions.Item label="Renkler">{item.colors}</Descriptions.Item>}{item.size && <Descriptions.Item label="Boyut">{item.size}</Descriptions.Item>}{item.notes && <Descriptions.Item label="Notlar">{item.notes}</Descriptions.Item>}</Descriptions></List.Item>} className="bg-white" />,
      });
    }

    // Technical Drawings
    if (product.technicalSpec.technicalDrawingUrls && product.technicalSpec.technicalDrawingUrls.length > 0) {
      items.push({
        key: 'techDrawings',
        label: 'Teknik Çizimler',
        children: (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {product.technicalSpec.technicalDrawingUrls.map((url, index) => renderFilePreview(url, index, 'Teknik Çizim'))}
          </div>
        ),
      });
    }

    // Measurement Guides
    if (product.technicalSpec.measurementGuideUrls && product.technicalSpec.measurementGuideUrls.length > 0) {
      items.push({
        key: 'measureGuides',
        label: 'Ölçüm Şekli Görselleri',
        children: (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {product.technicalSpec.measurementGuideUrls.map((url, index) => renderFilePreview(url, index, 'Ölçüm Şekli'))}
          </div>
        ),
      });
    }

    // Model Documents
    if (product.technicalSpec.modelDocumentUrls && product.technicalSpec.modelDocumentUrls.length > 0) {
      items.push({
        key: 'modelDocuments',
        label: 'Model Dosyaları',
        children: (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {product.technicalSpec.modelDocumentUrls.map((url, index) => renderFilePreview(url, index, 'Model Dosyası'))}
          </div>
        ),
      });
    }

    // Care Instructions
    if (product.technicalSpec.careInstructions && product.technicalSpec.careInstructions.length > 0) {
      items.push({
        key: 'careInstructions',
        label: 'Bakım Talimatları',
        children: <List size="small" bordered dataSource={product.technicalSpec.careInstructions} renderItem={(item) => <List.Item>{item}</List.Item>} className="bg-white" />,
      });
    }

    // Quality Standards
    if (product.technicalSpec.qualityStandards && product.technicalSpec.qualityStandards.length > 0) {
      items.push({
        key: 'qualityStandards',
        label: 'Kalite Standartları',
        children: <List size="small" bordered dataSource={product.technicalSpec.qualityStandards} renderItem={(item) => <List.Item>{item}</List.Item>} className="bg-white" />,
      });
    }

    // Notes
    if (product.technicalSpec.notes) {
      items.push({
        key: 'notes',
        label: 'Notlar',
        children: <p className="text-gray-700 bg-gray-50 p-4 rounded border">{product.technicalSpec.notes}</p>,
      });
    }

    return items;
  };

  // --- Media Gallery Renderer ---
  const renderImageGrid = (title: string, urls: string[]) => {
    if (!urls || urls.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`${title} bulunmamaktadır.`} />;
    }
    return (
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-700 mb-3">{title}</h4>
        <Image.PreviewGroup>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {urls.map((url, index) => {
              const fullUrl = getFullUrl(url);
              const isPdf = url.toLowerCase().endsWith('.pdf');
              return (
                <div key={`${title}-${index}`} className="aspect-square border rounded-md overflow-hidden hover:shadow-md transition-shadow flex justify-center items-center bg-gray-50 p-1">
                  {isPdf ? (
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center text-center text-blue-600 hover:text-blue-800">
                      <FilePdfOutlined style={{ fontSize: '36px' }} className="text-red-500 mb-1" />
                      <span className="text-xs break-all">{url.split('/').pop()}</span>
                    </a>
                  ) : (
                    <Image
                      src={fullUrl}
                      alt={`${title} ${index + 1}`}
                      className="object-contain max-h-full max-w-full"
                      preview={{ mask: <><EyeOutlined /> Önizle</> }}
                      placeholder={<Spin size="small" />}
                      fallback="/placeholder-image.png"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Image.PreviewGroup>
      </div>
    );
  };

  // --- Final Render ---
  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 sm:mb-0">{product.modelName}</h1>
          <Space wrap>
            {/* Edit Button - Conditionally render based on permission */}
            {hasPermission('products', "update") && (
                <AntButton icon={<EditOutlined />} onClick={() => navigate(`/products/${product._id}/edit/`)}>
                    Düzenle
                </AntButton>
            )}
            {/* Delete Button - Conditionally render based on permission */}
            {hasPermission('products', "delete") && (
                <AntButton icon={<DeleteOutlined />} danger onClick={() => setDeleteModalOpen(true)}>
                    Sil
                </AntButton>
            )}
            {/* PDF Download Button */}
            <AntButton icon={<DownloadOutlined />} href={`${API_URL}/products/${id}/pdf`} target="_blank">
                PDF İndir
            </AntButton>
            {/* PDF Preview Button */}
            <AntButton icon={<EyeOutlined />} onClick={handlePreviewPdf}>
                PDF Önizle
            </AntButton>
          </Space>
        </div>

        {/* Tabs */}
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Genel Bilgiler" key="1">
            {/* Grid for Basic Info and Images */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
              {/* Left Column: Basic Info */}
              <div>
                <SectionTitle>Temel Bilgiler</SectionTitle>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Model Adı">{product.modelName}</Descriptions.Item>
                  <Descriptions.Item label="Satın Alma Grubu">
                    <Tag color={getPurchasingGroupColor(product.purchasingGroup)}>
                      {formatPurchasingGroup(product.purchasingGroup)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Model Stili">{product.modelStyle || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Kumaş">{product.fabric}</Descriptions.Item> {/* TODO: Fetch fabric details */}
                  <Descriptions.Item label="Renk">{renderColorInfo()}</Descriptions.Item>
                 
                  <Descriptions.Item label="Ürün Tipi">
                    <Tag color={product.isPrototype ? 'geekblue' : 'green'}>
                      {product.isPrototype ? 'Prototip' : 'Normal Ürün'}
                    </Tag>
                  </Descriptions.Item>
                  {product.isPrototype && product.prototypeCode && (
                    <Descriptions.Item label="Prototip Kodu">{product.prototypeCode}</Descriptions.Item>
                  )}
                  {!product.isPrototype && product.productCode && (
                    <Descriptions.Item label="Ürün Kodu">{product.productCode}</Descriptions.Item>
                  )}
                  <Descriptions.Item label="Durum">
                    <Tag className={getStatusColor(product.status)}>
                      {formatStatus(product.status)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Açıklama">{product.description || '-'}</Descriptions.Item>
                </Descriptions>
              </div>

              {/* Right Column: Images */}
              <div>
                <SectionTitle>Ürün Görselleri</SectionTitle>
                {productImages.length > 0 ? (
                  <Carousel arrows prevArrow={<LeftOutlined />} nextArrow={<RightOutlined />} ref={carouselRef} dots={{ className: "bg-gray-200 rounded-full p-1" }}>
                    {productImages.map((url, index) => (
                      <div key={index} className="relative group flex justify-center items-center bg-gray-100 h-96"> {/* Adjust height as needed */}
                        <Image
                          src={getFullUrl(url)}
                          alt={`Ürün Görseli ${index + 1}`}
                          className="object-contain h-full max-h-96 mx-auto"
                          preview={{
                            mask: <EyeOutlined />,
                          }}
                        />
                      </div>
                    ))}
                  </Carousel>
                ) : (
                  <div className="flex justify-center items-center h-96 bg-gray-100 rounded"> {/* Match height */}
                    <Empty description="Ürün görseli bulunmamaktadır." />
                  </div>
                )}
              </div>
            </div>

            <Divider />

            {/* Aksesuarlar (Below the grid) */}
            <SectionTitle>Aksesuarlar</SectionTitle>
            <Table
              columns={[
                { title: 'Adı', dataIndex: 'name', key: 'name' },
                { title: 'Miktar', dataIndex: 'quantity', key: 'quantity' },
                { title: 'Açıklama', dataIndex: 'description', key: 'description', render: (text: string) => text || '-' },
              ]}
              dataSource={product.accessories.map((acc, index) => ({ ...acc, key: index }))} // Add key for Table
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="Aksesuar bulunmamaktadır." /> }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Teknik Föy" key="2">
            <Collapse defaultActiveKey={['sizeTable', 'fabricSpecs']} items={getTechnicalSpecItems()} className="mt-4" />
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="Ürün Dosyaları" key="3">
            <div className="mt-6">
              {product.technicalSpec?.modelDocumentUrls && product.technicalSpec.modelDocumentUrls.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {product.technicalSpec.modelDocumentUrls.map((url, index) => {
                    const fullUrl = getFullUrl(url);
                    const fileName = url.split('/').pop() || `Dosya ${index + 1}`;
                    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
                    
                    let fileIcon;
                    if (['pdf'].includes(fileExtension)) {
                      fileIcon = <FilePdfOutlined style={{ fontSize: '32px' }} className="text-red-500" />;
                    } else if (['doc', 'docx'].includes(fileExtension)) {
                      fileIcon = <FileOutlined style={{ fontSize: '32px' }} className="text-blue-500" />;
                    } else if (['xls', 'xlsx'].includes(fileExtension)) {
                      fileIcon = <FileExcelOutlined style={{ fontSize: '32px' }} className="text-green-500" />;
                    } else if (['zip', 'rar'].includes(fileExtension)) {
                      fileIcon = <FileZipOutlined style={{ fontSize: '32px' }} className="text-orange-500" />;
                    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
                      fileIcon = <FileImageOutlined style={{ fontSize: '32px' }} className="text-purple-500" />;
                    } else {
                      fileIcon = <FileOutlined style={{ fontSize: '32px' }} className="text-gray-500" />;
                    }
                    
                    return (
                      <a 
                        href={fullUrl}
                        key={`file-${index}`}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex flex-col items-center border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow bg-white"
                      >
                        {fileIcon}
                        <span className="mt-2 text-sm text-center text-gray-700 break-all line-clamp-2" title={fileName}>
                          {fileName}
                        </span>
                        <span className="mt-1 text-xs text-gray-500 uppercase">
                          {fileExtension}
                        </span>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <Empty description="Ürün dosyası bulunmamaktadır" />
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>

        {/* Image Modal */}
        <AntModal
          open={isImageModalOpen}
          onCancel={closeImageModal}
          footer={null}
          width="80vw"
          centered
          bodyStyle={{ padding: 0 }}
        >
          <Image
            src={getFullUrl(currentModalImage)}
            alt="Genişletilmiş Ürün Görseli"
            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            preview={false} // Disable nested preview
          />
        </AntModal>

        {/* Delete Confirmation Modal */}
        <AntModal
          title="Ürünü Sil"
          open={deleteModalOpen}
          onOk={handleDelete} // Use handleDelete directly
          onCancel={() => setDeleteModalOpen(false)}
          okText="Sil"
          cancelText="İptal"
          confirmLoading={loading} // Show loading state on OK button
          okButtonProps={{ danger: true }}
        >
          <p>"{product.modelName}" ürününü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
        </AntModal>
      </div>
    </div>
  );
};

export default ProductDetail;